<template>
    <div class="AnimatedSatellitesProgress" :style="style">
        <div class="hub" :style="hubStyle" />
        <div
            v-for="(s, i) of satellites"
            :key="phase === 'newSatellites' ? `new-${i}` : i"
            class="satelliteAnchor"
            :style="{ transform: `rotate(${s.rotation * 360}deg) translateX(${s.distance}em)` }"
            :class="{
                collapsing: phase === 'collapseSatellites',
                new: s.new,
                reachedCompletion: s.reachedCompletion,
                priorCompletion: s.priorCompletion
            }"
        >
            <div class="lineTrack" :style="{ transform: `rotate(-180deg) scale(${s.lineLength}, 1)` }">
                <div class="line" :style="{ transform: `scale(${s.lineProgress}, 1)` }" />
            </div>
            <div class="scaleAnchor">
                <div class="slotContainer" :style="{ transform: `rotate(-${s.rotation * 360}deg) scale(${s.scale})` }">
                    <slot name="satellite" :index="i" :progress="s.progress" />
                </div>
                <CircularProgress
                    :style="{ transform: `rotate(-${90 + (s.progress / 2) * 360}deg) scale(${s.scale})` }"
                    :track-color="trackColor"
                    :color="color"
                    :track-width="s.circleTrackWidth"
                    :stroke-width="s.circleStrokeWidth"
                    :progress="s.progress"
                />
            </div>
        </div>
    </div>
</template>

<script>
import CircularProgress from '@/components/CircularProgress';
import { wait } from '@/utils';

export default {
    components: { CircularProgress },
    props: {
        rotationSpeed: {
            type: Number,
            default: 0
        },
        trackColor: {
            type: String,
            default: '#aaa'
        },
        color: {
            type: String,
            default: 'orange'
        },
        priorSatelliteCount: {
            type: Number,
            default: 3
        },
        satelliteCount: {
            type: Number,
            default: 3
        },
        satelliteDistance: {
            type: Number,
            default: 2
        },
        satelliteRadius: {
            type: Number,
            default: 1.75
        },
        hubRadius: {
            type: Number,
            default: 3
        },
        priorProgress: {
            type: Number,
            default: 0
        },
        progress: {
            type: Number,
            default: 0.6
        },
        autoplay: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            hubRotation: 0,
            phase: 'initial'
        };
    },
    computed: {
        style() {
            return {
                '--local-hubRadius': this.hubRadius + 'em',
                '--local-satelliteRadius': this.satelliteRadius + 'em',
                '--local-trackColor': this.trackColor,
                '--local-color': this.color
            };
        },
        hubStyle() {
            return {
                width: this.hubRadius * 2 + 'em',
                height: this.hubRadius * 2 + 'em',
                left: -this.hubRadius + 'em',
                top: -this.hubRadius + 'em'
            };
        },
        satellites() {
            const phase = this.phase;
            const satellites = [];
            const satelliteCount =
                this.progress < 1 || phase === 'newSatellites' ? this.satelliteCount : this.priorSatelliteCount;
            const rotationDelta = 1 / satelliteCount;

            if (phase === 'newSatellites') {
                for (let i = 0; i < satelliteCount; i++) {
                    satellites.push({
                        rotation: rotationDelta * i + this.hubRotation,
                        distance: this.hubRadius + this.satelliteDistance + this.satelliteRadius,
                        lineLength: this.satelliteDistance,
                        lineProgress: 0,
                        progress: 0,
                        scale: 1,
                        new: true,
                        reachedCompletion: false,

                        circleTrackWidth: (4 * this.satelliteRadius) / 2.5,
                        circleStrokeWidth: (6 * this.satelliteRadius) / 2.5
                    });
                }
                return satellites;
            }

            const progressFraction = 1 / satelliteCount;
            let totalProgress = 0;
            if (phase === 'initial') {
                totalProgress = this.priorProgress;
            } else if (phase === 'newSatellites') {
                //
            } else {
                totalProgress = this.progress;
            }

            let totalPriorProgress = this.priorProgress;

            const distance =
                phase === 'collapseSatellites' ? 0 : this.hubRadius + this.satelliteDistance + this.satelliteRadius;

            for (let i = 0; i < satelliteCount; i++) {
                const priorProgress = Math.min(Math.max(totalPriorProgress / progressFraction, 0), 1);
                const progress = Math.min(Math.max(totalProgress / progressFraction, 0), 1);
                satellites.push({
                    rotation: rotationDelta * i + this.hubRotation,
                    distance,
                    lineLength: phase === 'collapseSatellites' ? 0 : this.satelliteDistance,
                    lineProgress: progress,
                    progress: progress,
                    priorCompletion: priorProgress === 1,
                    scale: phase === 'collapseSatellites' ? this.hubRadius / this.satelliteRadius : 1,
                    new: phase === 'newSatellites',
                    reachedCompletion: phase === 'growLines' && progress === 1 && priorProgress < 1,

                    circleTrackWidth: (4 * this.satelliteRadius) / 2.5,
                    circleStrokeWidth: (6 * this.satelliteRadius) / 2.5
                });
                totalProgress -= progressFraction;
                totalPriorProgress -= progressFraction;
            }
            return satellites;
        }
    },
    async mounted() {
        this.reset();
        this.$emit('onReady', {
            play: this.play,
            step: this.step,
            rewind: this.reset,
            demoRotation: this.demoRotation
        });
        if (this.autoplay) {
            await wait(500);
            this.play();
        }
    },
    methods: {
        async play() {
            while (this.animationSteps.length) {
                await this.animationSteps.shift()();
            }
        },
        step() {
            if (this.animationSteps.length) {
                return this.animationSteps.shift()();
            }
        },
        reset() {
            const steps = [];
            if (this.priorProgress < this.progress) {
                steps.push(this.animateSatelliteProgress);
            }
            if (this.progress === 1) {
                steps.push(this.animateCompletion);
            }
            if (this.progress === 1 && this.satelliteCount) {
                steps.push(this.animateRevealNewSatellites);
            }

            this.phase = 'initial';
            this.animationSteps = steps;
        },
        async animateSatelliteProgress() {
            this.phase = 'growLines';
            await wait(1000);
            if (this.satellites.some(s => s.reachedCompletion)) {
                await wait(1000);
            }
        },
        async animateCompletion() {
            this.phase = 'collapseSatellites';
            await wait(1000);
            this.$emit('onSatellitesCollapse');
            await wait(1000);
        },
        async animateRevealNewSatellites() {
            this.phase = 'newSatellites';
            await wait(1000);
        },
        demoRotation() {
            this.hubRotation += 0.05;
        }
    }
};
</script>

<style lang="scss" scoped>
$satelliteDistance: 4em;
$satelliteRadius: 1em;
$trackColor: rgba(white, 0.5);
$color: red;

.AnimatedSatellitesProgress {
    position: relative;
    width: 0;
    height: 0;
}
.hub {
    border: 1px solid var(--local-trackColor);
    border-radius: 50%;
    position: absolute;

    display: none;
}

.satelliteAnchor {
    position: absolute;
    width: 0;
    height: 0;
    transition: transform 1s;

    &.collapsing {
        .scaleAnchor {
            animation: fadeOut 1s;
            animation-delay: 1s;
            animation-fill-mode: forwards;
            @keyframes fadeOut {
                0% {
                    // opacity: 1;
                    // transform: translateX(0) scale(1.5);
                }
                100% {
                    opacity: 0;
                    transform: scale(2);
                }
            }
        }
    }
    &.new {
        .lineTrack {
            animation: fadeIn 1s;
            animation-fill-mode: both;
            @keyframes fadeIn {
                0% {
                    opacity: 0;
                }
                100% {
                    opacity: 1;
                }
            }
        }
        .scaleAnchor {
            animation: scaleIn 1s;
            animation-fill-mode: both;
            @keyframes scaleIn {
                0% {
                    opacity: 0;
                    transform: scale(0);
                }
                100% {
                    opacity: 1;
                    transform: scale(1);
                }
            }
        }
    }
    &.priorCompletion {
        .scaleAnchor {
            // border: 1px solid yellow;
        }
    }
    &.reachedCompletion {
        .scaleAnchor {
            animation: scaleInOut 0.5s;
            animation-delay: 1s;
            @keyframes scaleInOut {
                0% {
                    transform: scale(1);
                }
                50% {
                    transform: scale(1.2);
                }
                100% {
                    transform: scale(1);
                }
            }
        }
    }
}

.lineTrack {
    position: absolute;
    top: -1px;

    left: 0;
    left: -$satelliteRadius;
    left: calc(var(--local-satelliteRadius) * -1);

    background-color: $trackColor;
    background-color: var(--local-trackColor);
    transform-origin: left center;

    height: 2px;
    width: 1em;
    transition: transform 1s;
}
.line {
    height: 2px;
    background-color: var(--local-color);
    transform-origin: left center;
}

.scaleAnchor {
    transform: scale(1.0001);
}

.slotContainer {
    position: absolute;
    transition: transform 1s;

    width: $satelliteRadius * 2;
    width: calc(var(--local-satelliteRadius) * 2);
    height: $satelliteRadius * 2;
    height: calc(var(--local-satelliteRadius) * 2);

    top: -$satelliteRadius;
    top: calc(var(--local-satelliteRadius) * -1);
    left: -$satelliteRadius;
    left: calc(var(--local-satelliteRadius) * -1);
}

.CircularProgress {
    position: absolute;
    transition: transform 1s linear;

    width: $satelliteRadius * 2;
    width: calc(var(--local-satelliteRadius) * 2);
    height: $satelliteRadius * 2;
    height: calc(var(--local-satelliteRadius) * 2);

    top: -$satelliteRadius;
    top: calc(var(--local-satelliteRadius) * -1);
    left: -$satelliteRadius;
    left: calc(var(--local-satelliteRadius) * -1);
}
</style>
